// ========================================================================
// FILE: src/pages/Home.jsx
// PENJELASAN: Halaman utama publik yang menampilkan peringkat komisi mitra
//             dan informasi mengenai program kemitraan.
// ========================================================================
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaTrophy, FaMedal, FaChartLine, FaBook, FaUsers, FaArrowRight } from 'react-icons/fa';
import { getAllSales, getAllMitras } from '../supabase/database'; // <-- PATH DIPERBAIKI

const FeatureCard = ({ icon, title, children }) => (
  <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-xl transition-shadow">
    <div className="text-4xl text-amber-500 mb-4">{icon}</div>
    <h3 className="text-xl font-bold text-gray-800 mb-2">{title}</h3>
    <p className="text-gray-600">{children}</p>
  </div>
);

const StepCard = ({ number, title, children }) => (
    <div className="relative pl-12">
        <div className="absolute left-0 top-0 h-10 w-10 bg-amber-500 text-white font-bold text-xl rounded-full flex items-center justify-center">
            {number}
        </div>
        <h3 className="text-xl font-bold text-gray-800 mb-2">{title}</h3>
        <p className="text-gray-600">{children}</p>
    </div>
);

const Home = () => {
  const [rankings, setRankings] = useState([]);
  const [loading, setLoading] = useState(true);

  // Function to censor kode_mitra (e.g., "MT001" -> "M***1")
  const censorKodeMitra = (kode) => {
    if (!kode || kode.length <= 2) return kode;
    const first = kode.charAt(0);
    const last = kode.charAt(kode.length - 1);
    const middle = '*'.repeat(Math.max(kode.length - 2, 3));
    return `${first}${middle}${last}`;
  };

  // Function to censor nama_mitra (e.g., "Budi Santoso" -> "B*** S***")
  const censorName = (name) => {
    if (!name) return 'N***';
    const words = name.split(' ');
    return words.map(word => {
      if (word.length <= 1) return word;
      return word.charAt(0) + '*'.repeat(Math.max(word.length - 1, 3));
    }).join(' ');
  };

  // Function to censor komisi amount (e.g., "903160" -> "9**.***")
  const censorKomisi = (amount) => {
    const amountStr = amount.toString();
    if (amountStr.length <= 1) return amountStr;

    const firstDigit = amountStr.charAt(0);
    const remainingLength = amountStr.length - 1;

    // Create pattern like "9**.***"
    if (remainingLength <= 3) {
      return `${firstDigit}${'*'.repeat(remainingLength)}`;
    } else if (remainingLength <= 6) {
      return `${firstDigit}${'*'.repeat(2)}.${'*'.repeat(Math.max(remainingLength - 2, 3))}`;
    } else {
      return `${firstDigit}${'*'.repeat(2)}.${'*'.repeat(3)}`;
    }
  };

  useEffect(() => {
    const calculateRankings = async () => {
      try {
        setLoading(true);
        console.log('Calculating rankings...');
        
        // Get all sales and mitra data
        const salesData = await getAllSales();
        const mitraData = await getAllMitras();
        
        console.log('Sales data:', salesData?.length || 0, 'records');
        console.log('Mitra data:', mitraData?.length || 0, 'records');
        
        if (!salesData || !mitraData) {
          console.log('Missing data, setting empty rankings');
          setRankings([]);
          return;
        }

        // Calculate komisi per mitra (tarif global), hanya dari pesanan yang lunas
        const komisiByMitra = {};
        
        salesData.forEach(sale => {
          // Hanya hitung pesanan yang sudah lunas dan divalidasi
          if (!sale.kode_mitra || 
              sale.pembayaran !== 'lunas' || 
              sale.status === 'ditolak' || 
              sale.status === 'menunggu validasi') {
            return;
          }
          
          if (!komisiByMitra[sale.kode_mitra]) {
            komisiByMitra[sale.kode_mitra] = {
              totalKomisi: 0,
              totalPenjualan: 0,
              totalOmset: 0
            };
          }
          
          // Hitung komisi berdasarkan tarif global (15%) dan subtotal
          const subtotal = parseFloat(sale.subtotal) || 0;
          const persentaseKomisi = 15; // tarif global 15%
          const komisi = (subtotal * persentaseKomisi) / 100;
          
          komisiByMitra[sale.kode_mitra].totalKomisi += komisi;
          komisiByMitra[sale.kode_mitra].totalPenjualan += 1;
          komisiByMitra[sale.kode_mitra].totalOmset += subtotal;
        });

        // Merge with mitra data and create ranking
        const ranking = Object.keys(komisiByMitra).map(kodeMitra => {
          const mitra = mitraData.find(m => m.kode_mitra === kodeMitra);
          const stats = komisiByMitra[kodeMitra];
          
          return {
            kode_mitra: censorKodeMitra(kodeMitra),
            nama_mitra_censored: censorName(mitra?.nama || mitra?.kode_mitra || 'Unknown'),
            omset_censored: censorKomisi(stats.totalKomisi),
            totalKomisi: stats.totalKomisi,
            totalPenjualan: stats.totalPenjualan,
            totalOmset: stats.totalOmset
          };
        });

        // Sort by total komisi (descending) and take top 10
        const topRanking = ranking
          .filter(item => item.totalKomisi > 0) // Hanya tampilkan yang ada komisinya
          .sort((a, b) => b.totalKomisi - a.totalKomisi)
          .slice(0, 10);

        console.log('Top ranking calculated:', topRanking.length, 'entries');
        console.log('Sample ranking data:', topRanking.slice(0, 3));
        setRankings(topRanking);
        
      } catch (error) {
        console.error("Gagal menghitung peringkat:", error);
        setRankings([]); // Set empty array instead of crashing
      } finally {
        setLoading(false);
      }
    };
    calculateRankings();
  }, []);

  const getMedalColor = (index) => {
    if (index === 0) return 'text-yellow-400';
    if (index === 1) return 'text-gray-400';
    if (index === 2) return 'text-amber-600';
    return 'text-gray-300';
  };

  return (
    <div className="bg-gray-50 w-full">
      {/* Hero Section */}
      <section className="text-center py-16 px-4">
        <div className="mb-8">
            <h1 className="text-4xl md:text-5xl font-extrabold text-gray-800 tracking-tight">Selamat Datang</h1>
            <p className="text-lg text-gray-600 mt-2 max-w-2xl mx-auto">di Dashboard Kemitraan NH Press. Bergabunglah bersama kami dan raih kesuksesan bersama.</p>
        </div>
      
        {/* Peringkat Mitra Section */}
        <div id="peringkat" className="w-full max-w-3xl mx-auto bg-white rounded-2xl shadow-lg p-6 md:p-8">
          <div className="flex items-center gap-4 mb-4">
            <FaTrophy className="text-4xl text-amber-500" />
            <h2 className="text-3xl font-bold text-gray-800">Peringkat Mitra</h2>
          </div>
          <p className="text-gray-600 mb-6">Peringkat berdasarkan total omset yang telah divalidasi.</p>
          
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500 mr-3"></div>
              <p className="text-gray-600">Memuat peringkat...</p>
            </div>
          ) : rankings.length > 0 ? (
            <div className="space-y-3">
              {rankings.map((mitra, index) => (
                <div key={index} className="flex items-center bg-gray-100 p-4 rounded-lg transition hover:bg-gray-200 hover:shadow-sm">
                  <div className="flex items-center w-16">
                    <span className={`text-2xl font-bold ${getMedalColor(index)}`}>
                      <FaMedal />
                    </span>
                    <span className="text-lg font-semibold text-gray-700 ml-2">#{index + 1}</span>
                  </div>
                  <div className="flex-grow">
                    <div className="flex items-center gap-3">
                      <span className="bg-amber-500 text-white px-2 py-1 rounded text-sm font-bold">
                        {mitra.kode_mitra}
                      </span>
                      <span className="font-medium text-gray-800">{mitra.nama_mitra_censored}</span>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {mitra.totalPenjualan} transaksi lunas
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-green-600">
                      Rp {mitra.omset_censored}
                    </div>
                    <div className="text-xs text-gray-500">komisi</div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <FaChartLine className="text-6xl text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">Belum ada data peringkat.</p>
              <p className="text-gray-400 text-sm mt-2">Peringkat akan muncul setelah ada transaksi penjualan.</p>
            </div>
          )}
        </div>
      </section>

      {/* Mengapa Bergabung Section */}
      <section id="mengapa" className="py-20 px-4 bg-white">
          <div className="max-w-5xl mx-auto">
              <h2 className="text-3xl font-bold text-center text-gray-800 mb-12">Mengapa Bergabung dengan Kemitraan NH Press?</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  <FeatureCard icon={<FaChartLine />} title="Komisi Menarik">
                      Dapatkan komisi sebesar 15% dari setiap penjualan buku yang berhasil Anda validasi.
                  </FeatureCard>
                  <FeatureCard icon={<FaBook />} title="Produk Berkualitas">
                      Pasarkan buku-buku terbitan Nur Hidayah Press yang berkualitas dan diminati banyak sekolah.
                  </FeatureCard>
                  <FeatureCard icon={<FaUsers />} title="Dukungan Penuh">
                      Kami menyediakan sistem dashboard yang mudah digunakan untuk memantau penjualan dan komisi Anda.
                  </FeatureCard>
              </div>
          </div>
      </section>

      {/* Cara Kerja Section */}
      <section id="cara-kerja" className="py-20 px-4 bg-gray-50">
          <div className="max-w-5xl mx-auto">
              <h2 className="text-3xl font-bold text-center text-gray-800 mb-16">Sangat Mudah untuk Memulai</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
                  <StepCard number="1" title="Daftar & Login">
                      Admin akan mendaftarkan akun Anda. Setelah itu, Anda bisa langsung masuk ke sistem.
                  </StepCard>
                  <StepCard number="2" title="Input Penjualan">
                      Masukkan data setiap penjualan yang berhasil Anda lakukan melalui form yang tersedia.
                  </StepCard>
                  <StepCard number="3" title="Dapatkan Komisi">
                      Setelah penjualan divalidasi oleh admin, komisi akan langsung tercatat di akun Anda.
                  </StepCard>
              </div>
          </div>
      </section>

      {/* Call to Action Section */}
      <section id="gabung" className="bg-amber-500">
        <div className="max-w-4xl mx-auto text-center py-16 px-4">
            <h2 className="text-3xl font-bold text-white">Siap Meningkatkan Penghasilan Anda?</h2>
            <p className="text-white/90 mt-4 mb-8">Login sekarang untuk mulai mengelola penjualan dan melihat potensi pendapatan Anda bersama NH Press.</p>
            <div className="space-y-4">
              <Link to="/login" className="bg-white text-amber-600 font-bold py-3 px-6 rounded-lg text-lg hover:bg-gray-100 transition-colors inline-flex items-center gap-2 border-2 border-white hover:border-gray-100">
                  <span>Masuk Sekarang</span>
                  <FaArrowRight />
              </Link>
            </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
